import { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { Button } from "../../components/ui/button";
import { Input }  from "../../components/ui/input";
import { Label }  from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Store, Landmark, CreditCard, Receipt, Bell, ShieldCheck, Users,
  Save, Plus, Trash2, Pencil, X, Crown, Briefcase, Wrench, ScanLine,
  Image as ImageIcon, Upload, Zap, Cloud, CloudOff, Database,
  RefreshCcw, CheckCircle2, XCircle, AlertTriangle, ArrowRightLeft, Shield,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "store",         label: "Store Info",    icon: Store        },
  { key: "bank",          label: "Bank Details",  icon: Landmark     },
  { key: "payments",      label: "Payments",      icon: CreditCard   },
  { key: "invoice",       label: "Invoice",       icon: Receipt      },
  { key: "notifications", label: "Notifications", icon: Bell         },
  { key: "policies",      label: "Policies",      icon: ShieldCheck  },
  { key: "accounts",      label: "Accounts",      icon: Users        },
  { key: "pinnacle",      label: "⚡ Pinnacle",    icon: Zap          },
];

export default function AdminSettings() {
  const [tab, setTab] = useState("store");
  const [s, setS]     = useState(null);
  const { refresh: refreshSettings } = useSettings();

  const load = () => adminApi.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  if (!s) return (
    <div className="font-mono text-xs uppercase tracking-widest" style={{ color:"#94a3b8" }}>
      Loading settings…
    </div>
  );

  const onSaved = (d) => { setS(d); refreshSettings(); };

  return (
    <div className="space-y-5" data-testid="admin-settings-page">
      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>
          System
        </div>
        <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Settings</h1>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          Store configuration · staff accounts · Pinnacle integration
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-2 flex items-center gap-1 overflow-x-auto shadow-sm"
        style={{ border:"1px solid #dde3ef" }} data-testid="settings-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`settings-tab-${t.key}`}
            className="px-4 h-9 text-[11px] font-mono uppercase tracking-widest rounded-xl flex items-center gap-2 transition-colors whitespace-nowrap"
            style={tab === t.key
              ? { background:"#e31e24", color:"#ffffff", fontWeight:700 }
              : { color:"#64748b" }
            }>
            <t.icon className="h-3.5 w-3.5"/> {t.label}
          </button>
        ))}
      </div>

      {tab === "store"         && <StoreInfo         s={s} setS={onSaved} />}
      {tab === "bank"          && <BankDetails       s={s} setS={setS} />}
      {tab === "payments"      && <Payments          s={s} setS={setS} />}
      {tab === "invoice"       && <InvoiceSettings   s={s} setS={setS} />}
      {tab === "notifications" && <Notifications     s={s} setS={setS} />}
      {tab === "policies"      && <Policies          s={s} setS={setS} />}
      {tab === "accounts"      && <Accounts />}
      {tab === "pinnacle"      && <PinnacleSettings  s={s} />}
    </div>
  );
}

/* ── Shared helpers ──────────────────────────────────────────────── */
const inp = { background:"#ffffff", borderColor:"#dde3ef", color:"#0f172a" };
const cardCls = "bg-white rounded-2xl shadow-sm";
const cardStyle = { border:"1px solid #dde3ef" };

function SectionSave({ patch, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.put("/admin/settings/extended", patch);
      onSaved?.(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={save} disabled={busy}
      className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
      style={{ background:"#e31e24" }} data-testid="settings-save-btn">
      <Save className="h-4 w-4"/> {busy ? "Saving…" : "Save changes"}
    </button>
  );
}

/* ── Logo Upload ─────────────────────────────────────────────────── */
function LogoUpload({ currentLogo, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy]       = useState(false);
  const [preview, setPreview] = useState(currentLogo);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 2 * 1024 * 1024)    { toast.error("Image must be under 2MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await adminApi.post("/admin/settings/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data.logo_url);
      onUploaded(data.logo_url);
      toast.success("Logo updated");
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <div className={`${cardCls} p-5`} style={cardStyle} data-testid="logo-upload-section">
      <div className="text-[11px] font-mono uppercase tracking-widest mb-4 flex items-center gap-2" style={{ color:"#64748b" }}>
        <ImageIcon className="h-4 w-4"/> Store Logo
      </div>
      <div className="flex items-center gap-8 flex-wrap">
        <div className="w-48 h-24 rounded-xl flex items-center justify-center overflow-hidden p-3 shrink-0"
          style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
          {preview
            ? <img src={preview} alt="Logo" className="max-w-full max-h-full object-contain" onError={(e)=>{ e.target.style.display="none"; }}/>
            : <span className="text-[12px] font-mono" style={{ color:"#94a3b8" }}>No logo</span>
          }
        </div>
        <div className="flex-1 min-w-[200px]">
          <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" ref={fileRef} hidden onChange={upload}/>
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide"
            style={{ background:"#e31e24" }}>
            <Upload className="h-4 w-4"/> {busy ? "Uploading…" : "Upload New Logo"}
          </button>
          <p className="text-[12px] mt-3 leading-relaxed" style={{ color:"#64748b" }}>
            PNG, JPG, SVG or WebP · Max 2MB<br/>
            Updates header, footer, sidebar and emails instantly.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Store Info ──────────────────────────────────────────────────── */
function StoreInfo({ s, setS }) {
  const { refresh: refreshGlobal } = useSettings();
  return (
    <div className="space-y-4 max-w-3xl">
      <LogoUpload currentLogo={s.logo_url || "/logo.png"} onUploaded={(url) => { setS({ ...s, logo_url: url }); refreshGlobal(); }}/>
      <div className={`${cardCls} p-5 space-y-4`} style={cardStyle} data-testid="settings-store-section">
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["store_name",  "Store name",  "BCP Brisbane Car Parts"],
            ["abn",         "ABN",         "12 345 678 900"],
            ["store_email", "Store email", "sales@bcpcarparts.com.au"],
            ["store_phone", "Phone",       "0406 291 986"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>{label}</Label>
              <Input value={s[k]||""} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })}
                className="mt-1 rounded-xl" style={inp} data-testid={`settings-${k}`}/>
            </div>
          ))}
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Address</Label>
          <textarea value={s.store_address||""} onChange={(e) => setS({ ...s, store_address: e.target.value })}
            rows={3} placeholder="Brisbane, QLD 4000"
            className="w-full mt-1 px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
            style={{ ...inp, border:"1px solid #dde3ef" }}/>
        </div>
        <SectionSave patch={{ store_name:s.store_name, abn:s.abn, store_email:s.store_email, store_phone:s.store_phone, store_address:s.store_address }}
          onSaved={(d) => { setS(d); refreshGlobal(); }}/>
      </div>
    </div>
  );
}

/* ── Bank Details ────────────────────────────────────────────────── */
function BankDetails({ s, setS }) {
  return (
    <div className={`${cardCls} p-5 space-y-4 max-w-3xl`} style={cardStyle} data-testid="settings-bank-section">
      <div className="grid md:grid-cols-2 gap-4">
        {[["bank_name","Bank"],["bank_account_name","Account name"],["bank_bsb","BSB"],["bank_account","Account number"]].map(([k, label]) => (
          <div key={k}>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>{label}</Label>
            <Input value={s[k]||""} onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className="mt-1 rounded-xl" style={inp} data-testid={`settings-${k}`}/>
          </div>
        ))}
      </div>
      <div className="text-[12px]" style={{ color:"#64748b" }}>Shown on bank-transfer invoices and manual orders.</div>
      <SectionSave patch={{ bank_name:s.bank_name, bank_account_name:s.bank_account_name, bank_bsb:s.bank_bsb, bank_account:s.bank_account }} onSaved={(d) => setS(d)}/>
    </div>
  );
}

/* ── Payments ────────────────────────────────────────────────────── */
function Payments({ s, setS }) {
  const toggles = [
    ["walkin_cash_enabled",   "Walk-in cash",                   "For in-store walk-in sales"],
    ["walkin_card_enabled",   "Walk-in card (manual)",          "Card terminal — no online charge"],
    ["bank_transfer_enabled", "Bank transfer",                  "Show bank details on invoice"],
    ["cod_enabled",           "Cash on Delivery",               "COD orders + Pinnacle export"],
    ["stripe_enabled",        "Stripe (online + payment links)","Auto-fill default to ON"],
  ];
  return (
    <div className={`${cardCls} p-5 space-y-4 max-w-3xl`} style={cardStyle} data-testid="settings-payments-section">
      <div className="space-y-3">
        {toggles.map(([k, label, hint]) => (
          <div key={k} className="flex items-center justify-between p-3 rounded-xl"
            style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
            <div>
              <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>{label}</div>
              <div className="text-[11px]" style={{ color:"#64748b" }}>{hint}</div>
            </div>
            <Switch checked={k==="cod_enabled"||k==="stripe_enabled"?s[k]!==false:!!s[k]}
              onCheckedChange={(v) => setS({ ...s, [k]: v })}
              data-testid={`payment-toggle-${k}`}/>
          </div>
        ))}
      </div>
      <div className="pt-4" style={{ borderTop:"1px solid #eef2f8" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color:"#94a3b8" }}>Stock Policy</div>
        <div className="flex items-start justify-between p-3 rounded-xl" style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
          <div className="flex-1 pr-4">
            <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>Allow Backorders</div>
            <div className="text-[11px] mt-0.5" style={{ color:"#64748b" }}>
              {s.allow_backorder
                ? "ON — orders accepted even when stock is 0. Order marked as backorder."
                : "OFF — orders blocked when stock < qty requested."}
            </div>
            {s.allow_backorder && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest" style={{ color:"#d97706" }}>
                ⚠ Backorder mode active — oversell is possible.
              </div>
            )}
          </div>
          <Switch checked={!!s.allow_backorder} onCheckedChange={(v) => setS({ ...s, allow_backorder: v })}
            data-testid="payment-toggle-allow-backorder"/>
        </div>
      </div>
      <SectionSave patch={{ walkin_cash_enabled:!!s.walkin_cash_enabled, walkin_card_enabled:!!s.walkin_card_enabled, bank_transfer_enabled:!!s.bank_transfer_enabled, cod_enabled:s.cod_enabled!==false, stripe_enabled:s.stripe_enabled!==false, allow_backorder:!!s.allow_backorder }} onSaved={(d) => setS(d)}/>
    </div>
  );
}

/* ── Invoice Settings ────────────────────────────────────────────── */
function InvoiceSettings({ s, setS }) {
  return (
    <div className={`${cardCls} p-5 space-y-4 max-w-3xl`} style={cardStyle} data-testid="settings-invoice-section">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Invoice number prefix</Label>
          <Input value={s.invoice_prefix||""} placeholder="BCP-" onChange={(e) => setS({ ...s, invoice_prefix: e.target.value })}
            className="mt-1 rounded-xl" style={inp} data-testid="settings-invoice-prefix"/>
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>GST rate</Label>
          <Input type="number" step="0.01" value={s.tax_rate??0.1} onChange={(e) => setS({ ...s, tax_rate: Number(e.target.value) })}
            className="mt-1 rounded-xl" style={inp} data-testid="settings-tax-rate"/>
        </div>
      </div>
      <div>
        <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Invoice footer / terms</Label>
        <textarea rows={4} value={s.invoice_footer||""} onChange={(e) => setS({ ...s, invoice_footer: e.target.value })}
          placeholder="Warranty 30–90 days. Returns within 14 days. Thank you for your business."
          className="w-full mt-1 px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
          style={{ ...inp, border:"1px solid #dde3ef" }}/>
      </div>
      <SectionSave patch={{ invoice_prefix:s.invoice_prefix, tax_rate:Number(s.tax_rate)||0.1, invoice_footer:s.invoice_footer }} onSaved={(d) => setS(d)}/>
    </div>
  );
}

/* ── Notifications ───────────────────────────────────────────────── */
function Notifications({ s, setS }) {
  const items = [
    ["notify_new_order",   "New order",        "Email admin when a new order is placed"],
    ["notify_new_quote",   "New quote / offer","Email admin when a customer submits a quote or offer"],
    ["notify_new_message", "New chat message", "Email admin when a customer sends a chat message"],
    ["notify_low_stock",   "Low stock",        "Daily digest when products fall below threshold"],
  ];
  return (
    <div className={`${cardCls} p-5 space-y-4 max-w-3xl`} style={cardStyle} data-testid="settings-notifications-section">
      <div className="space-y-3">
        {items.map(([k, label, hint]) => (
          <div key={k} className="flex items-center justify-between p-3 rounded-xl"
            style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
            <div>
              <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>{label}</div>
              <div className="text-[11px]" style={{ color:"#64748b" }}>{hint}</div>
            </div>
            <Switch checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`notify-toggle-${k}`}/>
          </div>
        ))}
      </div>
      <div className="pt-4" style={{ borderTop:"1px solid #eef2f8" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color:"#94a3b8" }}>After-hours auto-reply</div>
        <textarea rows={3} value={s.after_hours_message||""} onChange={(e) => setS({ ...s, after_hours_message: e.target.value })}
          placeholder="Thanks for messaging BCP! We're closed right now. We'll reply first thing tomorrow."
          className="w-full px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
          style={{ ...inp, border:"1px solid #dde3ef" }} data-testid="after-hours-msg"/>
      </div>
      <div className="pt-4" style={{ borderTop:"1px solid #eef2f8" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color:"#94a3b8" }}>Google Review Link</div>
        <input type="url" value={s.google_review_link||""} onChange={(e) => setS({ ...s, google_review_link: e.target.value })}
          placeholder="https://g.page/r/YOUR_BUSINESS_ID/review"
          className="w-full px-3 py-2 rounded-xl text-[13px] outline-none"
          style={{ ...inp, border:"1px solid #dde3ef" }} data-testid="settings-google-review-link"/>
        <div className="text-[11px] mt-1" style={{ color:"#94a3b8" }}>Get your link from Google Business Profile → Get more reviews.</div>
      </div>
      <div className="text-[12px]" style={{ color:"#94a3b8" }}>Email notifications require Resend API key in .env to go live.</div>
      <SectionSave patch={{ notify_new_order:!!s.notify_new_order, notify_new_quote:!!s.notify_new_quote, notify_new_message:!!s.notify_new_message, notify_low_stock:!!s.notify_low_stock, after_hours_message:s.after_hours_message, google_review_link:s.google_review_link||"" }} onSaved={(d) => setS(d)}/>
    </div>
  );
}

/* ── Policies ────────────────────────────────────────────────────── */
function Policies({ s, setS }) {
  return (
    <div className={`${cardCls} p-5 space-y-4 max-w-3xl`} style={cardStyle} data-testid="settings-policies-section">
      {[
        ["shipping_policy", "Shipping Policy",  "Same-day dispatch. Free shipping over $250 to AU metro."],
        ["warranty_policy", "Warranty Policy",  "30–90 day warranty depending on category."],
        ["return_policy",   "Returns Policy",   "30-day returns on most parts. Damaged items not eligible."],
        ["privacy_policy",  "Privacy Policy",   "We collect personal information to process your order and contact you about it..."],
        ["refund_policy",   "Refund Policy",    "Refunds are processed within 5–7 business days of receiving the returned item..."],
      ].map(([k, label, ph]) => (
        <div key={k}>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>{label}</Label>
          <textarea rows={4} value={s[k]||""} onChange={(e) => setS({ ...s, [k]: e.target.value })}
            placeholder={ph} className="w-full mt-1 px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
            style={{ ...inp, border:"1px solid #dde3ef" }} data-testid={`policy-${k}`}/>
        </div>
      ))}
      <SectionSave patch={{
        shipping_policy: s.shipping_policy, warranty_policy: s.warranty_policy,
        return_policy: s.return_policy, privacy_policy: s.privacy_policy, refund_policy: s.refund_policy,
      }} onSaved={(d) => setS(d)}/>
    </div>
  );
}

/* ── Accounts ────────────────────────────────────────────────────── */
function Accounts() {
  const [sub, setSub] = useState("staff");
  return (
    <div className="space-y-4" data-testid="settings-accounts-section">
      <div className="flex items-center gap-1.5">
        {[["staff","Staff Accounts",Users],["roles","Roles & Permissions",ShieldCheck]].map(([k,label,Icon])=>(
          <button key={k} onClick={()=>setSub(k)} data-testid={`accounts-tab-${k}`}
            className="flex items-center gap-2 h-9 px-4 rounded-xl text-[11px] font-mono uppercase tracking-widest"
            style={sub===k?{background:"#0f172a",color:"#ffffff"}:{background:"#f5f7fb",border:"1px solid #dde3ef",color:"#64748b"}}>
            <Icon className="h-3.5 w-3.5"/> {label}
          </button>
        ))}
      </div>
      {sub === "staff" ? <StaffAccounts/> : <RolesPermissions/>}
    </div>
  );
}

const ROLE_ICONS = { super_admin:Crown, part_navigator:ScanLine, sales:Briefcase, workshop:Wrench };

function StaffAccounts() {
  const [data, setData]   = useState({ items:[], stats:{ total:0, online:0, roles:0, inactive:0 } });
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const [s, r] = await Promise.all([adminApi.get("/admin/staff"), adminApi.get("/admin/roles")]);
    setData(s.data); setRoles(r.data.items||[]);
  };
  useEffect(()=>{ load(); },[]);

  const filtered = data.items.filter(m=>!q||m.name.toLowerCase().includes(q.toLowerCase())||m.email.toLowerCase().includes(q.toLowerCase()));

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    await adminApi.delete(`/admin/staff/${m.id}`);
    toast.success("Staff removed"); load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[{v:data.stats.total,l:"Staff total",c:"#1e5bb8"},{v:data.stats.online,l:"Online now",c:"#16a34a"},{v:data.stats.roles,l:"Roles",c:"#7c3aed"},{v:data.stats.inactive,l:"Inactive",c:"#e31e24"}].map(({v,l,c})=>(
          <div key={l} className={`${cardCls} p-4`} style={{ ...cardStyle, borderTop:`3px solid ${c}` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{l}</div>
            <div className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search staff…"
          className="rounded-xl flex-1" style={inp} data-testid="staff-search"/>
        <button onClick={()=>setEditing("new")}
          className="flex items-center gap-1.5 h-10 px-4 rounded-xl text-white text-[12px] font-bold uppercase tracking-wide whitespace-nowrap"
          style={{ background:"#16a34a" }} data-testid="staff-new-btn">
          <Plus className="h-4 w-4"/> New Staff Account
        </button>
      </div>
      <div className={`${cardCls} overflow-x-auto`} style={cardStyle}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Staff member","Role","Last login","Status","Actions"].map((h,i)=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em", textAlign:i>=4?"right":"left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((m,idx)=>{
              const Icon = ROLE_ICONS[m.role?.key]||Users;
              return (
                <tr key={m.id} className="hover:bg-[#f5f7fb] transition-colors"
                  style={{ borderBottom:idx<filtered.length-1?"1px solid #f0f4f8":"none" }}
                  data-testid={`staff-row-${m.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center font-heading font-bold text-[11px] text-white"
                        style={{ background:"#1e5bb8" }}>
                        {m.name.split(" ").map(p=>p[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-[13px] font-medium" style={{ color:"#0f172a" }}>{m.name}</div>
                        <div className="text-[11px]" style={{ color:"#64748b" }}>{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {m.role && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                        style={m.role.key==="super_admin"
                          ? { background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" }
                          : m.role.key==="sales"
                          ? { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }
                          : { background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" }
                        }>
                        <Icon className="h-3 w-3"/> {m.role.name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px]" style={{ color:"#64748b" }}>
                    {m.last_login_at ? new Date(m.last_login_at).toLocaleString("en-AU") : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={m.status==="active"?{ background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }:{ background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0" }}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={()=>setEditing(m)} title="Edit"
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}
                        data-testid={`staff-edit-${m.email}`}><Pencil className="h-3.5 w-3.5"/></button>
                      <button onClick={()=>remove(m)} title="Remove"
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background:"#fee2e2", color:"#e31e24", border:"1px solid #fca5a5" }}
                        data-testid={`staff-delete-${m.email}`}><Trash2 className="h-3.5 w-3.5"/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length===0 && (
              <tr><td colSpan={5} className="p-12 text-center text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                No staff accounts yet — click New Staff Account
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <StaffModal staff={editing==="new"?null:editing} roles={roles} onClose={()=>setEditing(null)} onSaved={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function StaffModal({ staff, roles, onClose, onSaved }) {
  const [form, setForm] = useState({ name:staff?.name||"", email:staff?.email||"", role_id:staff?.role_id||roles[0]?.id||"", password:"", status:staff?.status||"active" });
  const [busy, setBusy] = useState(false);
  const isNew = !staff;

  const submit = async (e) => {
    e.preventDefault();
    if (isNew && (form.password||"").length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const body = { name:form.name, email:form.email, role_id:form.role_id, status:form.status };
      if (form.password) body.password = form.password;
      if (isNew) await adminApi.post("/admin/staff", body);
      else       await adminApi.put(`/admin/staff/${staff.id}`, body);
      toast.success(isNew?"Staff added":"Staff updated"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail||"Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-2xl"
        style={cardStyle} onClick={e=>e.stopPropagation()} data-testid="staff-modal">
        <div className="flex items-center justify-between">
          <div className="font-heading text-xl font-bold" style={{ color:"#0f172a" }}>{isNew?"New staff account":"Edit staff"}</div>
          <button type="button" onClick={onClose} style={{ color:"#94a3b8" }}><X className="h-5 w-5"/></button>
        </div>
        {[["name","Name","text",true],["email","Email","email",true]].map(([k,label,type,req])=>(
          <div key={k}>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>{label}</Label>
            <Input type={type} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
              required={req} className="mt-1 rounded-xl" style={inp} data-testid={`staff-${k}-input`}/>
          </div>
        ))}
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Role</Label>
          <Select value={form.role_id} onValueChange={v=>setForm({...form,role_id:v})}>
            <SelectTrigger className="mt-1 rounded-xl" style={inp} data-testid="staff-role-select"><SelectValue/></SelectTrigger>
            <SelectContent>{roles.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
            {isNew?"Password":"New password (optional)"}
          </Label>
          <Input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}
            placeholder={isNew?"Min 8 chars":"Leave blank to keep"} required={isNew}
            className="mt-1 rounded-xl" style={inp} data-testid="staff-password-input"/>
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Status</Label>
          <Select value={form.status} onValueChange={v=>setForm({...form,status:v})}>
            <SelectTrigger className="mt-1 rounded-xl" style={inp}><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </div>
        <button type="submit" disabled={busy}
          className="w-full h-11 rounded-xl text-white font-bold uppercase tracking-wide text-[13px] disabled:opacity-50"
          style={{ background:"#e31e24" }} data-testid="staff-save-btn">
          {busy?"Saving…":isNew?"Add staff":"Update"}
        </button>
      </form>
    </div>
  );
}

const PERMISSION_GROUPS = [
  { key: "dashboard",  label: "Dashboard",                        icon: "📊" },
  { key: "inventory",  label: "Inventory",                        icon: "📦" },
  { key: "orders",     label: "Orders",                           icon: "🛒" },
  { key: "customers",  label: "Customers",                        icon: "👥" },
  { key: "credits",    label: "Credits",                          icon: "💳" },
  { key: "invoices",   label: "Invoices",                         icon: "🧾" },
  { key: "shipping",   label: "Shipping",                         icon: "🚚" },
  { key: "requests",   label: "Requests (Quotes/Offers/Returns/B2B)", icon: "📋" },
  { key: "messages",   label: "Messages",                         icon: "💬" },
  { key: "sync",       label: "Sync Queue & Import/Export",        icon: "🔄" },
  { key: "audit",      label: "Audit Log & Email Log",            icon: "🔍" },
  { key: "settings",   label: "Settings & Staff Management",      icon: "⚙️" },
];

function RolesPermissions() {
  const [roles, setRoles]   = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await adminApi.get("/admin/roles"); setRoles(data.items||[]); };
  useEffect(()=>{ load(); },[]);
  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await adminApi.delete(`/admin/roles/${r.id}`); toast.success("Role removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail||"Failed"); }
  };
  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={()=>setEditing("new")}
          className="flex items-center gap-1.5 h-10 px-4 rounded-xl text-white text-[12px] font-bold uppercase tracking-wide"
          style={{ background:"#16a34a" }} data-testid="role-new-btn">
          <Plus className="h-4 w-4"/> New Role
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {roles.map(r=>{
          const Icon = ROLE_ICONS[r.key]||Users;
          const all  = r.permissions.includes("*");
          return (
            <div key={r.id} className={`${cardCls} p-5`} style={cardStyle} data-testid={`role-card-${r.key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background:"#eff6ff" }}><Icon className="h-5 w-5" style={{ color:"#1e5bb8" }}/></div>
                  <div>
                    <div className="font-heading font-bold text-[15px]" style={{ color:"#0f172a" }}>{r.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                      {r.is_default?"Default":"Custom"} · {all?"All permissions":`${r.permissions.length} permissions`}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={()=>setEditing(r)} title="Edit"
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}
                    data-testid={`role-edit-${r.key}`}><Pencil className="h-3.5 w-3.5"/></button>
                  {!r.is_default && (
                    <button onClick={()=>remove(r)} title="Delete"
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background:"#fee2e2", color:"#e31e24", border:"1px solid #fca5a5" }}
                      data-testid={`role-delete-${r.key}`}><Trash2 className="h-3.5 w-3.5"/></button>
                  )}
                </div>
              </div>
              {!all && r.permissions.length>0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {r.permissions.slice(0,6).map(p=>(
                    <span key={p} className="text-[10px] font-mono px-2 py-0.5 rounded-lg"
                      style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}>{p}</span>
                  ))}
                  {r.permissions.length>6 && <span className="text-[10px]" style={{ color:"#94a3b8" }}>+ {r.permissions.length-6} more</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {editing && <RoleModal role={editing==="new"?null:editing} onClose={()=>setEditing(null)} onSaved={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function RoleModal({ role, onClose, onSaved }) {
  const isNew  = !role;
  const [name, setName]   = useState(role?.name || "");
  const [perms, setPerms] = useState(new Set(role?.permissions || []));
  const [busy, setBusy]   = useState(false);

  const togglePerm = (k) => {
    const n = new Set(perms);
    if (n.has(k)) n.delete(k); else n.add(k);
    setPerms(n);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), permissions: Array.from(perms) };
      if (isNew) await adminApi.post("/admin/roles", body);
      else       await adminApi.put(`/admin/roles/${role.id}`, body);
      toast.success(isNew ? "Role created" : "Role updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const isSuperAdmin = role?.permissions?.includes("*");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <form onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4 shadow-2xl"
        style={cardStyle} onClick={e => e.stopPropagation()} data-testid="role-modal">

        <div className="flex items-center justify-between">
          <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>
            {isNew ? "New Role" : `Edit · ${role.name}`}
          </div>
          <button type="button" onClick={onClose} style={{ color: "#94a3b8" }}><X className="h-5 w-5"/></button>
        </div>

        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Role Name</Label>
          <Input value={name} onChange={e => setName(e.target.value)} required disabled={role?.is_default}
            className="mt-1 rounded-xl" style={inp} data-testid="role-name-input"/>
          {role?.is_default && (
            <div className="text-[11px] mt-1" style={{ color: "#94a3b8" }}>Default role names cannot be changed.</div>
          )}
        </div>

        {isSuperAdmin ? (
          <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
            <Crown className="h-5 w-5" style={{ color: "#d97706" }}/>
            <span className="text-[13px]" style={{ color: "#92400e" }}>Super Admin has all permissions — not editable.</span>
          </div>
        ) : (
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide mb-3 block" style={{ color: "#64748b" }}>
              Permissions — tap to toggle on/off
            </Label>
            <div className="space-y-2">
              {PERMISSION_GROUPS.map(g => {
                const on = perms.has(g.key);
                return (
                  <button key={g.key} type="button" onClick={() => togglePerm(g.key)}
                    data-testid={`role-perm-${g.key}`}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all"
                    style={on
                      ? { background: "#0b1a3b", color: "#fff", border: "2px solid #0b1a3b" }
                      : { background: "#f5f7fb", color: "#475569", border: "2px solid #dde3ef" }
                    }>
                    <span className="text-[16px]">{g.icon}</span>
                    <span className="text-[13px] font-medium flex-1">{g.label}</span>
                    <span className="text-[11px] font-mono font-bold"
                      style={{ color: on ? "#4ade80" : "#94a3b8" }}>
                      {on ? "✓ ON" : "OFF"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isSuperAdmin && (
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={busy}
              className="flex-1 h-10 rounded-xl text-white font-bold text-[13px] disabled:opacity-50"
              style={{ background: "#0b1a3b" }}>
              {busy ? "Saving…" : isNew ? "Create Role" : "Save Changes"}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 h-10 rounded-xl text-[13px] font-semibold"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PINNACLE INTEGRATION TAB
   Tests live connection to both Solera APIs and manages mode switch.
─────────────────────────────────────────────────────────────────── */
function PinnacleSettings({ s }) {
  const [mode,      setMode]     = useState(s?.adapter_mode || "csv");
  const [testing,   setTesting]  = useState(false);
  const [switching, setSwitching]= useState(false);
  const [result,    setResult]   = useState(null);
  const [expanded,  setExpanded] = useState(null);

  const testConnection = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await adminApi.post("/admin/pinnacle/test-connection");
      setResult(data);
      if (data.both_ok) toast.success("Both Pinnacle APIs connected!");
      else toast.error("One or both APIs failed — see details below");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const switchMode = async (target) => {
    if (target === "api" && !result?.both_ok) { toast.error("Run a successful connection test first"); return; }
    if (target === "csv" && !window.confirm("Revert to CSV mode?\n\nOrders will queue locally. You can switch back to API mode at any time.")) return;
    setSwitching(true);
    try {
      await adminApi.post(target === "api" ? "/admin/pinnacle/switch-to-api" : "/admin/pinnacle/switch-to-csv");
      setMode(target);
      toast.success(target === "api" ? "🎉 Switched to live Pinnacle API mode!" : "Reverted to CSV mode");
    } catch (e) { toast.error(e.response?.data?.detail || "Switch failed"); }
    finally { setSwitching(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="pinnacle-settings-tab">

      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Integration</div>
        <h2 className="font-heading text-[22px] font-bold" style={{ color:"#0f172a" }}>Pinnacle Professional API</h2>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          Live connection to Solera OPS API (inventory) and Order API (work orders). Currently in <b>{mode.toUpperCase()}</b> mode.
        </p>
      </div>

      {/* Current mode banner */}
      <div className="flex items-center gap-3 p-4 rounded-2xl"
        style={{ background: mode==="api"?"#dcfce7":"#fffbeb", border:`1px solid ${mode==="api"?"#86efac":"#fde68a"}` }}>
        {mode==="api"
          ? <Cloud className="h-6 w-6 shrink-0" style={{ color:"#16a34a" }}/>
          : <Database className="h-6 w-6 shrink-0" style={{ color:"#d97706" }}/>
        }
        <div className="flex-1">
          <div className="font-heading font-bold text-[15px] uppercase" style={{ color: mode==="api"?"#15803d":"#92400e" }}>
            {mode==="api" ? "Live API Mode" : "CSV / Offline Mode"}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: mode==="api"?"#16a34a":"#b45309" }}>
            {mode==="api"
              ? "Orders push to Pinnacle in real-time. Inventory syncs automatically."
              : "Orders queue locally. Use Import/Export page for manual CSV sync."}
          </div>
        </div>
        <span className="text-[10px] font-mono font-bold uppercase px-2 py-1 rounded-lg text-white"
          style={{ background: mode==="api"?"#16a34a":"#d97706" }}>
          {mode.toUpperCase()}
        </span>
      </div>

      {/* API credentials */}
      <div className={`${cardCls} overflow-hidden`} style={cardStyle}>
        <div className="px-5 py-3 font-heading font-bold text-[13px] uppercase tracking-wide"
          style={{ borderBottom:"1px solid #eef2f8", color:"#0f172a" }}>
          API Credentials
        </div>
        {[
          { label:"OPS API (Inventory)", note:"Pulls parts catalogue and stock levels from Pinnacle",
            url:"https://api.carparts-au.com/ops/v1/parts", user:"BCP_opsX",
            envUrl:"PINNACLE_OPS_URL", envUser:"PINNACLE_OPS_USERNAME", envPass:"PINNACLE_OPS_PASSWORD",
            icon:Database, accent:"#1e5bb8", key:"ops" },
          { label:"Order API (Work Orders)", note:"Pushes online orders as Unconfirmed Work Orders to Pinnacle",
            url:"http://api.carparts-au.com/order/orderapi", user:"oiBCPacc1",
            envUrl:"PINNACLE_ORDER_URL", envUser:"PINNACLE_ORDER_USERNAME", envPass:"PINNACLE_ORDER_PASSWORD",
            icon:ArrowRightLeft, accent:"#7c3aed", key:"order" },
        ].map(({ label, note, url, user, envUrl, envUser, envPass, icon:Icon, accent, key }) => (
          <div key={key} className="px-5 py-4 flex items-start gap-4" style={{ borderBottom:"1px solid #f0f4f8" }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background:`${accent}18` }}>
              <Icon className="h-4 w-4" style={{ color:accent }}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[14px] mb-0.5" style={{ color:"#0f172a" }}>{label}</div>
              <div className="text-[11px] mb-2" style={{ color:"#64748b" }}>{note}</div>
              <div className="space-y-0.5 text-[11px] font-mono">
                <div><span style={{ color:"#94a3b8" }}>{envUrl}=</span><span style={{ color:"#475569" }}>{url}</span></div>
                <div><span style={{ color:"#94a3b8" }}>{envUser}=</span><span style={{ color:"#475569" }}>{user}</span></div>
                <div><span style={{ color:"#94a3b8" }}>{envPass}=</span><span style={{ color:"#94a3b8" }}>••••••••</span></div>
              </div>
              <div className="mt-1 text-[10px]" style={{ color:"#94a3b8" }}>
                Set in <code style={{ background:"#f5f7fb", padding:"1px 4px", borderRadius:4 }}>backend/.env</code>
              </div>
            </div>
            {result && (
              <div className="shrink-0">
                {result[key]?.ok ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-lg"
                    style={{ background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }}>
                    <CheckCircle2 className="h-3 w-3"/> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-lg"
                    style={{ background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" }}>
                    <XCircle className="h-3 w-3"/> Failed
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Test connection */}
      <div className={`${cardCls} p-5 space-y-4`} style={cardStyle}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-bold text-[15px] uppercase" style={{ color:"#0f172a" }}>Connection Test</div>
            <div className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
              Verify both APIs respond before switching to live mode
            </div>
          </div>
          <button onClick={testConnection} disabled={testing}
            className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ background:"#1e5bb8" }} data-testid="pinnacle-test-btn">
            <RefreshCcw className={`h-4 w-4 ${testing?"animate-spin":""}`}/>
            {testing ? "Testing…" : "Test Connection"}
          </button>
        </div>

        {result && (
          <div className="space-y-3">
            {[{key:"ops",label:"OPS API (Inventory)"},{key:"order",label:"Order API (Work Orders)"}].map(({ key, label }) => {
              const r = result[key] || {};
              return (
                <div key={key} className="rounded-xl overflow-hidden" style={{ border:"1px solid #dde3ef" }}>
                  <div className="px-4 py-2.5 flex items-center justify-between"
                    style={{ background: r.ok ? "#f0fdf4" : "#fef2f2" }}>
                    <div className="flex items-center gap-2">
                      {r.ok
                        ? <CheckCircle2 className="h-4 w-4" style={{ color:"#16a34a" }}/>
                        : <XCircle className="h-4 w-4" style={{ color:"#e31e24" }}/>
                      }
                      <span className="font-semibold text-[13px]" style={{ color: r.ok?"#15803d":"#991b1b" }}>{label}</span>
                      {r.status > 0 && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md"
                          style={{ background:"rgba(0,0,0,0.06)", color:"#64748b" }}>
                          HTTP {r.status}
                        </span>
                      )}
                    </div>
                    {(r.sample || r.error) && (
                      <button onClick={() => setExpanded(expanded===key ? null : key)}
                        className="text-[11px] font-mono hover:underline" style={{ color:"#64748b" }}>
                        {expanded===key ? "Hide" : "Show"} raw response
                      </button>
                    )}
                  </div>
                  {r.error && (
                    <div className="px-4 py-2 text-[12px] font-mono" style={{ color:"#e31e24" }}>{r.error}</div>
                  )}
                  {expanded===key && (r.sample||r.error) && (
                    <pre className="px-4 py-3 text-[11px] font-mono overflow-auto max-h-40"
                      style={{ background:"#0d1117", color:"#4ade80" }}>
                      {r.sample||r.error}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status mapping reference */}
      <div className={`${cardCls} p-5`} style={cardStyle}>
        <div className="font-heading font-bold text-[14px] uppercase mb-3" style={{ color:"#0f172a" }}>
          Pinnacle ↔ BCP Status Mapping
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ borderBottom:"1px solid #eef2f8" }}>
                {["Pinnacle Panel (what staff see)","BCP Status","Meaning"].map(h=>(
                  <th key={h} className="text-left py-2 pr-4 font-mono font-semibold uppercase text-[10px] tracking-widest" style={{ color:"#94a3b8" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor:"#f0f4f8" }}>
              {[
                ["Unconfirmed Work Order", "confirmed", "New online/eBay order",    "#dbeafe","#1e40af"],
                ["Ready To Invoice",       "packed",    "Part picked, ready",        "#e0e7ff","#3730a3"],
                ["Today's Invoices/Credits","delivered","Invoiced and completed",    "#dcfce7","#15803d"],
                ["Today's Quotes",         "quoted",    "Quotation sent",            "#fef3c7","#92400e"],
                ["RMA",                    "return_approved","Return authorised",    "#fffbeb","#b45309"],
              ].map(([pinnacle, bcp, meaning, bg, color]) => (
                <tr key={pinnacle}>
                  <td className="py-2 pr-4 font-mono font-semibold text-[12px]" style={{ color:"#0f172a" }}>{pinnacle}</td>
                  <td className="py-2 pr-4">
                    <span className="text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-lg"
                      style={{ background:bg, color:color }}>{bcp}</span>
                  </td>
                  <td className="py-2" style={{ color:"#64748b" }}>{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Switch mode */}
      <div className={`${cardCls} p-5 space-y-3`} style={cardStyle}>
        <div className="font-heading font-bold text-[14px] uppercase" style={{ color:"#0f172a" }}>
          Switch Integration Mode
        </div>
        {mode !== "api" ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-xl"
              style={{ background: result?.both_ok?"#f0fdf4":"#f5f7fb", border:"1px solid #dde3ef" }}>
              {result?.both_ok
                ? <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#16a34a" }}/>
                : <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#d97706" }}/>
              }
              <div className="text-[12px]" style={{ color:"#475569" }}>
                {result?.both_ok
                  ? "Both APIs connected. You can safely switch to live mode."
                  : "Run a connection test above before switching to API mode."}
              </div>
            </div>
            <button onClick={() => switchMode("api")} disabled={!result?.both_ok || switching}
              className="w-full h-11 rounded-xl text-white font-bold uppercase tracking-wide text-[13px] flex items-center justify-center gap-2 disabled:opacity-40"
              style={{ background:"#16a34a" }} data-testid="switch-to-api-btn">
              <Zap className="h-4 w-4"/>
              {switching ? "Switching…" : "Switch to Live API Mode"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-xl"
              style={{ background:"#fffbeb", border:"1px solid #fde68a" }}>
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#d97706" }}/>
              <div className="text-[12px]" style={{ color:"#78350f" }}>
                Currently in live API mode. Reverting to CSV means orders queue locally until switched back.
              </div>
            </div>
            <button onClick={() => switchMode("csv")} disabled={switching}
              className="h-10 px-5 rounded-xl font-semibold text-[13px] flex items-center gap-2"
              style={{ background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" }}
              data-testid="switch-to-csv-btn">
              {switching ? "Reverting…" : "Revert to CSV mode (safe fallback)"}
            </button>
          </div>
        )}
      </div>

      {/* Security note */}
      <div className="flex items-start gap-3 p-4 rounded-2xl"
        style={{ background:"#fffbeb", border:"1px solid #fde68a" }}>
        <Shield className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#d97706" }}/>
        <div className="text-[12px]" style={{ color:"#78350f" }}>
          <strong>Security note:</strong> The Order API uses <code>http://</code> (not https).
          Order data travels unencrypted on that connection.
          Ask Solera for an HTTPS endpoint before going live with production orders.
        </div>
      </div>
    </div>
  );
}